/**
 * LISTEN TO A LIVE CALL, AND TAKE IT OVER. The panel's console for one call,
 * shown on the session page while the call is live (docs/hitl.md §7).
 *
 * All the behaviour is in `lib/call/live/monitor.ts`; this is the picture of
 * its snapshot. Three things on it are decisions rather than layout:
 *
 *   TAKING OVER HAS A CONFIRMATION STEP. It hangs up the AI, and there is no
 *   way to hand the call back. One click must not do that.
 *
 *   A REFUSAL IS SAID IN WORDS. "The caller's page is not connected for live
 *   audio" tells the person what happened; a code does not.
 *
 *   A READ-ONLY ACCOUNT IS TOLD, NOT OFFERED A BUTTON. The panel refuses the
 *   token request for a viewer (403 `read_only`), so the button is not drawn
 *   for one, and the server's refusal is shown plainly if the role changed
 *   under an open page.
 */
import { useEffect, useState, useSyncExternalStore } from 'react'
import { HeadphonesIcon, MicIcon, PhoneOffIcon } from 'lucide-react'
import { ApiError, monitorSession } from '@/api'
import { can } from '@/lib/auth'
import { useAuth } from '@/components/auth-provider'
import { Button } from '@/components/ui/button'
import {
  MonitorClient,
  audioReasonText,
  openBrowserMonitorAudio,
  type MonitorSnapshot,
  type MonitorVoice,
} from '@/lib/call/live/monitor'

const log = (level: 'info' | 'warn' | 'error', message: string) => {
  if (level === 'info') console.info(`[monitor] ${message}`)
  else console.warn(`[monitor] ${message}`)
}

function describeTokenError(e: unknown): { message: string; final: boolean; ended?: boolean } {
  if (e instanceof ApiError) {
    if (e.status === 403) {
      return { message: 'Your account has read-only access, so it cannot listen to live calls.', final: true }
    }
    if (e.status === 404) return { message: 'This call could not be found.', final: true }
    if (e.status === 409) return { message: 'The call has ended.', final: true, ended: true }
    return { message: `Could not reach the call: ${e.message}. Trying again…`, final: false }
  }
  return { message: `Could not reach the call: ${e instanceof Error ? e.message : String(e)}. Trying again…`, final: false }
}

const VOICE_LABEL: Record<MonitorVoice, string> = {
  caller: 'Caller',
  agent: 'Agent',
  human: 'Other staff',
}

function audioLine(s: MonitorSnapshot): string {
  switch (s.audio) {
    case 'starting':
      return 'Waiting for the call’s audio…'
    case 'flowing':
      return 'Audio is coming through.'
    case 'unavailable': {
      const why = audioReasonText(s.audioReason)
      return why ? `No live audio: ${why}.` : 'No live audio for this call.'
    }
    case 'stopped':
      return 'Live audio has stopped.'
    default:
      return ''
  }
}

function holderLine(s: MonitorSnapshot, me: string | null): string {
  if (s.holding) return 'You have the call. The caller hears your microphone.'
  if (s.takenOverBy !== null && s.takenOverBy === me) {
    return 'You took this call over, but this page is not connected to it. Pick it up again, or the call ends in about 30 seconds.'
  }
  if (s.takenOverBy !== null) return `${s.takenOverBy} has taken over this call.`
  return 'The AI has the call.'
}

export function LiveMonitor({ sessionId, onEnded }: { sessionId: string; onEnded?: () => void }) {
  const { user } = useAuth()
  const allowed = can(user, 'write')
  const [client] = useState(
    () =>
      new MonitorClient({
        getToken: (signal) => monitorSession(sessionId, signal),
        openAudio: () => openBrowserMonitorAudio(log),
        me: user?.email ?? null,
        describeTokenError,
        onLog: log,
      }),
  )
  const s = useSyncExternalStore(client.subscribe, client.getSnapshot)
  const [confirming, setConfirming] = useState(false)

  // Leaving the page stops listening. It does not end a call this console
  // holds: the DO waits for the holder to come back (docs/hitl.md §6).
  useEffect(() => () => client.close(), [client])

  const ended = s.phase === 'ended'
  useEffect(() => {
    if (ended) onEnded?.()
  }, [ended, onEnded])

  const active = s.phase === 'connecting' || s.phase === 'listening' || s.phase === 'reconnecting'
  const canOfferTakeover =
    s.phase === 'listening' && s.canTakeOver && !s.holding && s.takenOverBy === null && !s.takeoverPending
  /** This person holds the call, but this page lost it (a reload): offer it back. */
  const canResume =
    s.phase === 'listening' && !s.holding && user !== null && user !== undefined && s.takenOverBy === user.email

  return (
    <section className="panel">
      <h2>
        Live call
        <span className="note">listen in, or take the call over from the AI</span>
      </h2>
      <div className="body flex flex-col gap-3">
        {!allowed ? (
          <p className="text-sm text-muted-foreground">
            Your account has read-only access, so it cannot listen to live calls or take them over.
          </p>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2">
              {!active && !ended && (
                <Button size="sm" className="cursor-pointer" onClick={() => void client.listen()}>
                  <HeadphonesIcon aria-hidden />
                  Listen
                </Button>
              )}
              {active && !s.holding && (
                <Button size="sm" variant="outline" className="cursor-pointer" onClick={() => client.close()}>
                  Stop listening
                </Button>
              )}
              {canOfferTakeover && !confirming && (
                <Button size="sm" variant="outline" className="cursor-pointer" onClick={() => setConfirming(true)}>
                  <MicIcon aria-hidden />
                  Take over
                </Button>
              )}
              {canResume && (
                <Button size="sm" className="cursor-pointer" onClick={() => void client.resumeHolding()}>
                  <MicIcon aria-hidden />
                  Pick the call up again
                </Button>
              )}
              {s.holding && (
                <Button size="sm" variant="destructive" className="cursor-pointer" onClick={() => client.hangup()}>
                  <PhoneOffIcon aria-hidden />
                  End call
                </Button>
              )}
              <span className="text-sm text-muted-foreground">
                {s.phase === 'idle' && 'Not listening.'}
                {s.phase === 'connecting' && 'Connecting…'}
                {s.phase === 'reconnecting' && 'The connection dropped. Reconnecting…'}
                {s.phase === 'ended' && 'The call has ended.'}
                {s.phase === 'listening' && `${holderLine(s, user?.email ?? null)} ${audioLine(s)}`}
                {s.takeoverPending && ' Taking over…'}
              </span>
            </div>

            {confirming && canOfferTakeover && (
              <div className="max-w-2xl rounded-md border border-ev-warn-border bg-ev-warn-bg px-3 py-2 text-sm">
                <p>
                  <strong className="font-semibold">Take this call from the AI?</strong> The AI is hung
                  up, and the caller hears you through your microphone from then on. It cannot be
                  handed back to the AI.
                </p>
                <div className="mt-2 flex gap-2">
                  <Button
                    size="sm"
                    className="cursor-pointer"
                    onClick={() => {
                      setConfirming(false)
                      void client.takeOver()
                    }}
                  >
                    Yes, take over
                  </Button>
                  <Button size="sm" variant="outline" className="cursor-pointer" onClick={() => setConfirming(false)}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {s.error && <p className="err text-sm">{s.error}</p>}
            {s.takeoverProblem && <p className="text-sm text-ev-warn">{s.takeoverProblem}</p>}
            {s.playback === 'direct' && active && (
              <p className="max-w-2xl text-xs text-muted-foreground">
                This browser plays the call without echo cancellation, so if you take over, use
                headphones: otherwise the caller may hear themselves through your microphone.
              </p>
            )}

            {active && (
              <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                <span>Mute:</span>
                {(Object.keys(VOICE_LABEL) as MonitorVoice[]).map((voice) => (
                  <label key={voice} className="flex cursor-pointer items-center gap-1">
                    <input
                      type="checkbox"
                      checked={s.muted[voice]}
                      onChange={(e) => client.setMuted(voice, e.currentTarget.checked)}
                    />
                    {VOICE_LABEL[voice]}
                  </label>
                ))}
              </div>
            )}

            {s.transcript.length > 0 && (
              <div className="max-h-48 overflow-y-auto rounded-md border bg-muted/30 px-3 py-2 text-sm">
                {s.transcript.slice(-40).map((line) => (
                  <p key={line.itemId} className={line.final ? '' : 'text-muted-foreground'}>
                    <span className="font-medium">{line.who === 'user' ? 'Caller' : 'Agent'}:</span> {line.text}
                  </p>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </section>
  )
}
